import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Button, Tabs, Tab,
  TextField, CircularProgress, Alert, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Drawer, IconButton, Grid, FormControl, InputLabel, Select,
  MenuItem, Snackbar, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, FormHelperText, TablePagination, OutlinedInput, ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import VerifiedIcon from '@mui/icons-material/Verified';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import appConfig from '../../../utils/appConfig';
import { useAdmin } from '../../../AdminContext';
import banksData from '../../../data/banks.json';
import { getOrCreateSchedulingId } from '../../../utils/zohoService';
import lodLogo from '../../../assets/images/lodLogo.png';
import { useNavigate } from 'react-router-dom';
import AdminSettings, { SystemSettings } from './AdminSettings';

type Student = Record<string, string>;

interface Bank { id: number; name_he: string; name_en: string; }

interface CheckEntry {
  checkNumber: string;
  checkDate: string;
  checkAmount: string;
  bank: string;
  branchNumber: string;
  accountNumber: string;
}

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const SELECT_FIELDS: Record<string, string[]> = {
  'מגדר': ['זכר', 'נקבה'],
  'האם בהסעות': ['כן', 'לא'],
  'זכאות': ['זכאי', 'לא זכאי'],
};

const EDITABLE_GROUPS: { label: string; fields: string[] }[] = [
  { label: 'פרטי תלמיד', fields: ['שם פרטי', 'שם משפחה', 'מגדר', 'שכבה', 'תאריך לידה'] },
  { label: 'מוסד', fields: ['מוסד משובץ', 'שם מוסד משובץ'] },
  { label: 'כתובת', fields: ['שם רחוב', 'מספר בית', 'מספר דירה', 'שם עיר'] },
  { label: 'הורים', fields: ['שם אב', 'שם משפחה אב', 'טלפון נייד אב', 'תז אב', 'שם אם', 'שם משפחה אם', 'טלפון נייד אם', 'תז אם'] },
];

const CAN_REGISTER_FILTER_OPTIONS = [
  { value: 'כן', label: 'יכולים להירשם' },
  { value: 'לא', label: 'לא בהסעות' },
];
const ELIGIBILITY_FILTER_OPTIONS = [
  { value: 'זכאי', label: 'זכאים' },
  { value: 'לא זכאי', label: 'לא זכאים' },
];

const studentAddress = (s: Student) => {
  const street = s['שם רחוב'] || '';
  const houseNumber = s['מספר בית'] || '';
  const city = s['שם עיר'] || '';
  const streetLine = [street, houseNumber].filter(Boolean).join(' ');
  return [streetLine, city].filter(Boolean).join(', ');
};

const renderMultiSelectSummary = (selected: string[]) =>
  selected.length === 0 ? '' : selected.length <= 2 ? selected.join(', ') : `${selected.length} נבחרו`;

// ─── Status helpers ─────────────────────────────────────────────────────────

const canRegisterStudent = (s: Student) => s['האם בהסעות'] === 'כן';
const isEligible = (s: Student) => s['זכאות'] === 'זכאי';
const isRegistered = (s: Student) => !!s['האם נרשם'] && s['האם נרשם'] !== 'לא';

// ─── Student Row (memoized — avoids re-rendering all rows on every selection change) ──

interface StudentRowProps {
  student: Student;
  selected: boolean;
  onToggle: (id: string) => void;
  onEdit: (student: Student) => void;
}

const StudentRow: React.FC<StudentRowProps> = React.memo(({ student: s, selected, onToggle, onEdit }) => {
  const canReg = canRegisterStudent(s);
  const elig = isEligible(s);
  const reg = isRegistered(s);
  const id = s['תעודת זהות'];
  return (
    <TableRow hover
      sx={{ cursor: 'pointer', bgcolor: selected ? '#e3f2fd' : canReg ? 'inherit' : '#fafafa', opacity: canReg ? 1 : 0.6 }}
      onClick={() => onToggle(id)}>
      <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
        <Checkbox checked={selected} onChange={() => onToggle(id)} size="small" />
      </TableCell>
      <TableCell>{s['תעודת זהות']}</TableCell>
      <TableCell>{s['שם פרטי']}</TableCell>
      <TableCell>{s['שם משפחה']}</TableCell>
      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Tooltip title={s['שם מוסד משובץ']}><span>{s['שם מוסד משובץ']}</span></Tooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Tooltip title={studentAddress(s)}><span>{studentAddress(s) || '—'}</span></Tooltip>
      </TableCell>
      <TableCell>{s['שכבה']}</TableCell>
      <TableCell>
        <Chip size="small" label={canReg ? 'כן' : 'לא'}
          color={canReg ? 'primary' : 'default'} variant={canReg ? 'filled' : 'outlined'} />
      </TableCell>
      <TableCell>
        {canReg ? (
          <Chip size="small" label={elig ? 'זכאי' : 'לא זכאי'}
            color={elig ? 'success' : 'warning'} />
        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
      <TableCell>
        {canReg && !elig && s['סכום לתשלום'] ? (
          <Chip size="small" label={`${s['סכום לתשלום']} ₪`} color="info" />
        ) : canReg && !elig ? (
          <Chip size="small" label="לא הוגדר" color="error" variant="outlined" />
        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip size="small"
            label={reg ? `נרשם` : 'לא'}
            color={reg ? 'success' : 'default'}
            variant={reg ? 'filled' : 'outlined'} />
          {reg && /^\d+$/.test(s['האם נרשם']) && (
            <Tooltip title="פתח שיבוץ בזוהו">
              <IconButton size="small"
                onClick={() => window.open(appConfig.zoho.schedulingRecordUrl(s['האם נרשם']), '_blank')}>
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <IconButton size="small" color="primary" onClick={() => onEdit(s)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  bgColor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, color, bgColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: bgColor, borderRadius: 1, border: `1px solid ${color}30`, minWidth: 140 }}>
    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ color, lineHeight: 1.2 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{label}</Typography>
    </Box>
  </Box>
);

// ─── Admin Payment Tab ────────────────────────────────────────────────────────

interface AdminPaymentTabProps {
  adminToken: string;
  logout: () => void;
  onSnackbar: (msg: string, sev: 'success' | 'error') => void;
  settings: SystemSettings;
}

const banks: Bank[] = banksData as Bank[];

const emptyCheck = (): CheckEntry => ({
  checkNumber: '', checkDate: '', checkAmount: '', bank: '', branchNumber: '', accountNumber: '',
});

type PayStep = 'search' | 'setup' | 'method' | 'cash' | 'check';

const AdminPaymentTab: React.FC<AdminPaymentTabProps> = ({ adminToken, logout, onSnackbar, settings }) => {
  const navigate = useNavigate();

  // Step & student
  const [step, setStep] = useState<PayStep>('search');
  const [searchId, setSearchId] = useState('');
  const [student, setStudent] = useState<Student | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [existingScheduling, setExistingScheduling] = useState<any>(null);

  // Setup fields
  const [email, setEmail] = useState('');
  const [selectedStation, setSelectedStation] = useState('');
  const [schedulingId, setSchedulingId] = useState<string | null>(null);
  const [schedulingLoading, setSchedulingLoading] = useState(false);

  // Cash payment
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payAmount, setPayAmount] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // דיאלוג תוצאת תשלום — הצלחה/כשלון + לינק להורדת מסמך הקבלה מטרנזילה
  const [payResult, setPayResult] = useState<{
    success: boolean;
    message: string;
    retrievalKey?: string;
  } | null>(null);

  // Checks
  const [checks, setChecks] = useState<CheckEntry[]>([emptyCheck()]);
  const [commonBank, setCommonBank] = useState('');
  const [commonBranch, setCommonBranch] = useState('');
  const [commonAccount, setCommonAccount] = useState('');

  const totalChecksAmount = useMemo(
    () => checks.reduce((s, c) => s + (parseFloat(c.checkAmount) || 0), 0),
    [checks]
  );
  const duplicateCheckNumbers = useMemo(() => {
    const nums = checks.map(c => c.checkNumber).filter(Boolean);
    return new Set(nums.filter((n, i) => nums.indexOf(n) !== i));
  }, [checks]);

  // סכום החוב שניתן לגבות: בשיבוץ קיים - החוב שנותר, אחרת - הסכום לתשלום של התלמיד.
  // אם לא ידוע (0) לא חוסמים, כדי לא ליצור חסימה שגויה.
  const debtAmount = useMemo(() => {
    if (existingScheduling) return Number(existingScheduling.debt) || 0;
    return Number(student?.['סכום לתשלום']) || 0;
  }, [existingScheduling, student]);

  const cashExceedsDebt = debtAmount > 0 && Number(payAmount || 0) > debtAmount;
  const checksExceedDebt = debtAmount > 0 && totalChecksAmount > debtAmount;

  const reset = () => {
    setStep('search'); setSearchId(''); setStudent(null); setEmail('');
    setSelectedStation(''); setSchedulingId(null); setPayAmount(''); setPayNotes('');
    setChecks([emptyCheck()]); setFetchError(null); setExistingScheduling(null);
  };

  const findStudent = async () => {
    if (!adminToken || !searchId.trim()) return;
    setFetchLoading(true); setFetchError(null); setStudent(null); setExistingScheduling(null);
    try {
      const res = await fetch(`${appConfig.apiUrls.baseUrl}/sheets/student/${searchId.trim()}`, { headers: authHeaders(adminToken) });
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      if (res.ok) {
        setStudent(data);
        setPayAmount(data['סכום לתשלום'] || '');
        try {
          const schedRes = await fetch(
            `${appConfig.apiUrls.baseUrl}/zohoStudents/schedulingById/${searchId.trim()}/${settings.currentYear}`
          );
          const schedData = await schedRes.json();
          const existing = Array.isArray(schedData?.data) && schedData.data.length > 0 ? schedData.data[0] : null;
          setExistingScheduling(existing);
        } catch { /* non-fatal — fall back to the regular new-scheduling flow */ }
        setStep('setup');
      } else { setFetchError(data.error || 'תלמיד לא נמצא'); }
    } catch { setFetchError('שגיאת תקשורת'); }
    finally { setFetchLoading(false); }
  };

  // יש חוב רק אם debt > 0, למעט: הוראת קבע פעילה, או תלמיד זכאי (רשום) - שני
  // המקרים האלה נחשבים כאילו אין חוב. Payreceived אינו שדה אמין - הוורקפלואו
  // של "חשב סכומים" לא מעדכן אותו.
  const existingIsFreeEligible = existingScheduling?.field37 === 'זכאי';
  const existingHasDebt = !!existingScheduling && Number(existingScheduling.debt) > 0
    && existingScheduling.DirectDebit !== 'הוראת קבע פעילה' && !existingIsFreeEligible;
  const canContinueExisting = existingHasDebt;

  const continueExistingScheduling = () => {
    if (!existingScheduling || !email) return;
    setSelectedStation(existingScheduling.field36 || '');
    setSchedulingId(existingScheduling.id);
    setStep('method');
  };

  const handleCreateScheduling = async () => {
    if (!student || !selectedStation || !email) return;

    // Issue 1: block if amount is 0 or empty AND student is not eligible
    const amount = student['סכום לתשלום'];
    const isElig = student['זכאות'] === 'זכאי';
    if (!isElig && (!amount || amount === '0')) {
      onSnackbar('לא הוגדר סכום תשלום לתלמיד זה. יש לעדכן את פרטי התלמיד תחילה.', 'error');
      return;
    }

    setSchedulingLoading(true);
    const serverData = {
      name: `${student['שם פרטי']} ${student['שם משפחה']}`,
      institution: student['שם מוסד משובץ'],
      price: amount || '0',
      eligible: isElig ? 'זכאי' : 'לא זכאי',
    };
    try {
      // Issue 2: throwOnDuplicate + use settings year
      const id = await getOrCreateSchedulingId(
        serverData, student['תעודת זהות'], email, selectedStation, 'admin',
        { throwOnDuplicate: true, yearOverride: settings.currentYear }
      );
      if (id) { setSchedulingId(id); setStep('method'); }
    } catch (err: any) {
      const msg = err?.message?.startsWith('DUPLICATE_SCHEDULING:')
        ? err.message.replace('DUPLICATE_SCHEDULING: ', '')
        : 'שגיאה ביצירת שיבוץ בזוהו';
      onSnackbar(msg, 'error');
    } finally {
      setSchedulingLoading(false);
    }
  };

  const handleCreditCard = (method: 'pay_cc' | 'pay_hok_cc') => {
    if (!student || !schedulingId) return;
    const serverData = {
      name: `${student['שם פרטי']} ${student['שם משפחה']}`,
      institution: student['שם מוסד משובץ'],
      price: student['סכום לתשלום'] || '0',
      eligible: student['זכאות'] === 'זכאי' ? 'זכאי' : 'לא זכאי',
    };
    const state = { idNumber: student['תעודת זהות'], email, selectedStation, serverData, acceptTerms: true, schedulingId, amount: serverData.price, externalSchoolsForm: false, period: settings.currentYear };
    navigate(method === 'pay_cc' ? '/lod/creditcardPay' : '/lod/creditcardHok', { state });
  };

  // יוצר מסמך קבלה בטרנזילה (אותו פורמט כמו בזרימת התשלום של ההורים ב-CashOrCheckPay)
  // ומחזיר את מפתח האחזור להורדת המסמך. אם יצירת המסמך נכשלת — התשלום כולו נחסם
  // ולא נוצרת רשומת תשלום בזוהו (ראה handleRegisterPayment).
  const createTranzilaReceipt = async (
    payMethod: 'מזומן' | 'המחאה', amount: string
  ): Promise<{ retrievalKey?: string; documentNumber?: string; error?: string }> => {
    if (!student) return { error: 'לא נבחר תלמיד' };
    const authority = appConfig.authorities.find(a => a.en === 'lod');
    const clientName = `${student['שם פרטי']} ${student['שם משפחה']}`.trim();
    // הסרת גרשיים/גרש מהשנה כדי שלא ישבשו את קריאת הטקסט בקבלה
    const yearText = String(settings.currentYear || '').replace(/["'״׳]/g, '');
    const docData: any = {
      terminal_name: authority?.tranzilaCode,
      document_language: 'heb',
      document_currency_code: 'ILS',
      vat_percent: 17,
      client_address_line_1: '',
      client_id: student['תעודת זהות'],
      client_name: clientName,
      client_email: email,
      paymentType: payMethod,
      paymentAmount: amount,
      type: 'הסעות',
      year: yearText,
      // "עבור מה שולם" שיופיע בקבלה: הסעות - שם התלמיד, ת.ז, שנת לימודים (ללא גרשיים)
      receipt_paid_for: `הסעות - ${clientName}, ת.ז ${student['תעודת זהות']}, שנת ${yearText}`,
      payments: [] as any[],
    };
    if (payMethod === 'מזומן') {
      docData.payments.push({
        payment_method: 5,
        payment_date: payDate,
        amount,
        currency_code: 'ILS',
        other_description: `תשלום הסעה עבור ${clientName} על סך ${amount} שקלים`,
      });
    } else {
      checks.forEach(check => {
        docData.payments.push({
          payment_method: 3,
          payment_date: check.checkDate,
          amount: check.checkAmount,
          currency_code: 'ILS',
          bank: check.bank,
          branchNumber: check.branchNumber,
          accountNumber: check.accountNumber,
          checkNumber: check.checkNumber,
          other_description: `בנק: ${check.bank} סניף: ${check.branchNumber} מספר חשבון: ${check.accountNumber} מספר המחאה: ${check.checkNumber}`,
        });
      });
    }
    try {
      const res = await fetch(`${appConfig.apiUrls.baseUrl}/tranzilaRoutes/createDocument`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docData),
      });
      const data = await res.json().catch(() => null);
      const doc = data?.data?.document;
      const retrievalKey = doc?.retrieval_key;
      if (res.ok && retrievalKey) {
        // טרנזילה מחזירה את מספר הקבלה תחת המפתח number (למשל "32911"); document_number כחלופה.
        const documentNumber = doc?.number ?? doc?.document_number ?? null;
        return {
          retrievalKey,
          documentNumber: documentNumber != null ? String(documentNumber) : undefined,
        };
      }
      return { error: data?.error || data?.message || 'יצירת מסמך הקבלה בטרנזילה נכשלה' };
    } catch {
      return { error: 'שגיאת תקשורת מול השרת ביצירת מסמך הקבלה' };
    }
  };

  const handleRegisterPayment = async (payMethod: 'מזומן' | 'המחאה') => {
    if (!student || !adminToken) return;

    // ולידציה: אין לגבות מעל סכום החוב, ואין לרשום המחאות עם מספר כפול.
    if (payMethod === 'המחאה' && duplicateCheckNumbers.size > 0) {
      onSnackbar('קיימות המחאות עם אותו מספר — יש לתקן לפני הרישום', 'error');
      return;
    }
    const exceedsDebt = payMethod === 'מזומן' ? cashExceedsDebt : checksExceedDebt;
    if (exceedsDebt) {
      onSnackbar(`הסכום גבוה מהחוב בשיבוץ (${debtAmount.toLocaleString()} ₪) — לא ניתן לרשום`, 'error');
      return;
    }

    setPayLoading(true);
    const amount = payMethod === 'מזומן' ? payAmount : String(totalChecksAmount);

    // יצירת מסמך קבלה בטרנזילה לפני הרישום. אם הקבלה לא נוצרה — עוצרים כאן:
    // לא מעדכנים הערות ולא מקימים רשומת תשלום בזוהו, כי התשלום לא נקלט בטרנזילה.
    const doc = await createTranzilaReceipt(payMethod, amount);
    if (!doc.retrievalKey) {
      setPayResult({
        success: false,
        message: `התשלום לא נקלט בטרנזילה ולא נוצרה קבלה — לא נרשמה רשומת תשלום. ${doc.error || ''}`.trim(),
      });
      setPayLoading(false);
      return;
    }

    let notesEntry = '';
    if (payMethod === 'מזומן') {
      notesEntry = `תשלום מזומן | סכום: ${payAmount} ₪ | תאריך: ${payDate}`;
      if (payNotes) notesEntry += ` | הערה: ${payNotes}`;
    } else {
      notesEntry = `תשלום המחאות (${checks.length}) | סה"כ: ${totalChecksAmount} ₪ | תאריך: ${payDate}`;
      checks.forEach((c, i) => { notesEntry += `\n  המחאה ${i + 1}: מס' ${c.checkNumber} | תאריך ${c.checkDate} | ${c.checkAmount} ₪`; });
      if (payNotes) notesEntry += `\n  הערה: ${payNotes}`;
    }
    if (doc.documentNumber) notesEntry += `\n  מסמך טרנזילה: ${doc.documentNumber}`;
    const existingNotes = student['הערות'] || '';
    const updatedNotes = existingNotes ? `${existingNotes}\n${notesEntry}` : notesEntry;
    // 'האם נרשם' is already set to the scheduling record ID when the scheduling was created.
    const updateData = { 'הערות': updatedNotes };
    try {
      const res = await fetch(
        `${appConfig.apiUrls.baseUrl}/sheets/student/${student['תעודת זהות']}`,
        { method: 'PUT', headers: authHeaders(adminToken), body: JSON.stringify(updateData) }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      if (data.success) {
        // רושם "תשלום בפועל" בזוהו ומפעיל את חישוב הסכומים על השיבוץ.
        // מזומן/המחאה נחשבים "שולם" מיד עם קליטת התשלום ע"י האדמין.
        if (schedulingId) {
          const finalizePaymentPayload: any = {
            schedulingId, typePay: payMethod, amount, payDate, notes: payNotes,
            idCardHolder: student['תעודת זהות'],
            // שם המסוף (child_terminal בזוהו) - אותו מסוף שבו הופקה קבלת הטרנזילה.
            // נדרש גם לזיהוי כפילויות לפי מסוף.
            terminalName: appConfig.authorities.find(a => a.en === 'lod')?.tranzilaCode,
            // מספר הקבלה של טרנזילה נכנס גם לשדה המסמך (field6) וגם לשדה האסמכתא (asmahta).
            // field6 קצר; retrieval_key ארוך מדי (מעל 255) ולכן לא נשמר שם.
            ...(doc.documentNumber ? { docNumber: doc.documentNumber, asmahta: doc.documentNumber } : {}),
            // retrieval_key משמש לבניית לינק הורדת המסמך בשדה link_to_doc בזוהו
            retrievalKey: doc.retrievalKey,
          };
          if (payMethod === 'המחאה') {
            finalizePaymentPayload.checks = checks.map(c => ({
              checkNumber: c.checkNumber,
              checkDate: c.checkDate,
              checkAmount: c.checkAmount,
              bank: c.bank,
              branchNumber: c.branchNumber,
              accountNumber: c.accountNumber,
            }));
          }
          fetch(`${appConfig.apiUrls.baseUrl}/zohoPayments/finalizePayment`, {
            method: 'POST', headers: authHeaders(adminToken), body: JSON.stringify(finalizePaymentPayload),
          }).catch(err => console.error('Error finalizing payment in Zoho:', err));
        }
        setPayResult({
          success: true,
          message: `תשלום של ${amount} ₪ נרשם בהצלחה`,
          retrievalKey: doc.retrievalKey,
        });
      } else {
        setPayResult({
          success: false,
          message: data.error || 'שגיאה ברישום התשלום',
          retrievalKey: doc.retrievalKey,
        });
      }
    } catch {
      setPayResult({
        success: false,
        message: 'שגיאת תקשורת — התשלום לא נרשם',
        retrievalKey: doc.retrievalKey,
      });
    }
    finally { setPayLoading(false); }
  };

  const closePayResult = () => {
    const wasSuccess = payResult?.success;
    setPayResult(null);
    if (wasSuccess) reset();
  };

  const updateCheck = (index: number, field: keyof CheckEntry, value: string) =>
    setChecks(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));

  const updateCheckBank = (index: number, bankId: number) => {
    const b = banks.find(bk => bk.id === bankId);
    if (!b) return;
    setChecks(prev => prev.map((c, i) => i === index ? { ...c, bank: `מספר בנק: ${b.id},שם בנק: ${b.name_he}` } : c));
  };

  const addCheck = () => setChecks(prev => [...prev, {
    checkNumber: '', checkDate: '', checkAmount: '',
    bank: prev[0]?.bank || '',
    branchNumber: commonBranch || prev[0]?.branchNumber || '',
    accountNumber: commonAccount || prev[0]?.accountNumber || '',
  }]);

  const removeCheck = (index: number) => { if (checks.length > 1) setChecks(prev => prev.filter((_, i) => i !== index)); };

  const applyCommonToAll = () => {
    const bankStr = commonBank ? `מספר בנק: ${commonBank},שם בנק: ${banks.find(b => String(b.id) === commonBank)?.name_he || ''}` : '';
    setChecks(prev => prev.map(c => ({
      ...c,
      bank: bankStr || c.bank,
      branchNumber: commonBranch || c.branchNumber,
      accountNumber: commonAccount || c.accountNumber,
    })));
  };

  const getBankId = (bankStr: string): number | '' => {
    const match = bankStr?.match(/מספר בנק: (\d+)/);
    return match ? parseInt(match[1], 10) : '';
  };

  const isFreeEligible = !!student && student['זכאות'] === 'זכאי' &&
    (!student['סכום לתשלום'] || student['סכום לתשלום'] === '0');

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h6" fontWeight="bold">קליטת תשלום</Typography>
        {step !== 'search' && (
          <Button size="small" variant="outlined" onClick={reset}>חיפוש חדש</Button>
        )}
        {schedulingId && (
          <Chip label={`שיבוץ זוהו: ${schedulingId}`} size="small" color="success" variant="outlined" />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* ─ Left panel: student info ─ */}
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>חיפוש תלמיד</Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField fullWidth size="small" label="תעודת זהות" value={searchId}
                onChange={e => setSearchId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && findStudent()}
                disabled={step !== 'search'}
                inputProps={{ style: { textAlign: 'right' } }} />
              <Button variant="contained" onClick={findStudent}
                disabled={fetchLoading || !searchId.trim() || step !== 'search'}
                sx={{ minWidth: 50 }}>
                {fetchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              </Button>
            </Box>
            {fetchError && <Alert severity="error" sx={{ mb: 1 }}>{fetchError}</Alert>}

            {student && (
              <Box sx={{ bgcolor: '#f3f8ff', borderRadius: 1, p: 1.5, mt: 1 }}>
                <Typography fontWeight="bold">{student['שם פרטי']} {student['שם משפחה']}</Typography>
                <Typography variant="body2" color="text.secondary">ת"ז: {student['תעודת זהות']}</Typography>
                <Typography variant="body2" color="text.secondary">מוסד: {student['שם מוסד משובץ']}</Typography>
                <Typography variant="body2" color="text.secondary">שכבה: {student['שכבה']}</Typography>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  <Chip size="small" label={student['זכאות'] === 'זכאי' ? 'זכאי' : 'לא זכאי'}
                    color={student['זכאות'] === 'זכאי' ? 'success' : 'warning'} />
                  {student['סכום לתשלום'] && student['זכאות'] !== 'זכאי' && (
                    <Chip size="small" label={`${student['סכום לתשלום']} ₪`} color="info" />
                  )}
                  {student['האם נרשם'] && student['האם נרשם'] !== 'לא' && (
                    <Chip size="small" label="כבר נרשם" color="warning" />
                  )}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Existing scheduling for this year — read-only, no new details entered */}
          {student && step !== 'search' && existingScheduling && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                שיבוץ קיים לשנת {settings.currentYear}
              </Typography>
              <Typography variant="body2" color="text.secondary">תחנת עלייה: {existingScheduling.field36 || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">זכאות: {existingScheduling.field37 || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">סכום לחיוב: {existingScheduling.Annual_charge || '0'} ₪</Typography>
              <Typography variant="body2" color="text.secondary">
                שולם: {existingScheduling.payed ? `${existingScheduling.payed} ₪` : 'טרם שולם'}
              </Typography>
              <Typography variant="body2" color="text.secondary">סטטוס: {existingScheduling.status || '—'}</Typography>
              <Button size="small" startIcon={<OpenInNewIcon />} sx={{ mt: 1 }}
                onClick={() => window.open(appConfig.zoho.schedulingRecordUrl(existingScheduling.id), '_blank')}>
                פתח שיבוץ בזוהו
              </Button>

              {step === 'setup' && (canContinueExisting ? (
                <>
                  <TextField fullWidth size="small" label="אימייל לחשבונית *" type="email"
                    value={email} onChange={e => setEmail(e.target.value)}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ mt: 2, mb: 1.5 }} />
                  <Button fullWidth variant="contained" onClick={continueExistingScheduling} disabled={!email}>
                    המשך לתשלום עבור השיבוץ הקיים
                  </Button>
                </>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {existingIsFreeEligible
                    ? 'התלמיד זכאי ורשום ללא צורך בתשלום — לא ניתן להמשיך.'
                    : 'השיבוץ הזה כבר שולם — לא ניתן להמשיך.'}
                </Alert>
              ))}
            </Paper>
          )}

          {/* Station + Email — shown after student found, only when no scheduling exists yet */}
          {student && step !== 'search' && !existingScheduling && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>פרטי שיבוץ</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>תחנת עלייה *</InputLabel>
                <Select value={selectedStation} onChange={e => setSelectedStation(e.target.value)}
                  label="תחנת עלייה *" disabled={step === 'method' || step === 'cash' || step === 'check'}>
                  <MenuItem value=""><em>בחר תחנה</em></MenuItem>
                  {settings.stations.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth size="small" label="אימייל לחשבונית *" type="email"
                value={email} onChange={e => setEmail(e.target.value)}
                disabled={step === 'method' || step === 'cash' || step === 'check'}
                inputProps={{ style: { textAlign: 'right' } }}
                sx={{ mb: 1.5 }} />
              {step === 'setup' && (
                <Button fullWidth variant="contained" onClick={handleCreateScheduling}
                  disabled={schedulingLoading || !selectedStation || !email}>
                  {schedulingLoading ? <CircularProgress size={20} color="inherit" /> : 'יצירת שיבוץ והמשך לתשלום'}
                </Button>
              )}
              {schedulingId && (
                <Alert severity="success" sx={{ mt: 1 }}>שיבוץ נוצר בהצלחה</Alert>
              )}
            </Paper>
          )}
        </Box>

        {/* ─ Right panel: payment methods ─ */}
        {(step === 'method' || step === 'cash' || step === 'check') && (
          <Box sx={{ flex: 1 }}>

            {/* Method selection buttons */}
            {step === 'method' && (
              <Paper sx={{ p: 3 }}>
                {isFreeEligible ? (
                  <>
                    <Alert severity="success">
                      הרישום נקלט בהצלחה — התלמיד זכאי ואין צורך בתשלום.
                      {schedulingId && ` (שיבוץ זוהו: ${schedulingId})`}
                    </Alert>
                    <Button sx={{ mt: 2 }} onClick={reset}>חיפוש תלמיד נוסף</Button>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>בחר אמצעי תשלום</Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Button variant="outlined" size="large" sx={{ flex: 1, minWidth: 160, py: 2, flexDirection: 'column', gap: 0.5 }}
                        onClick={() => setStep('cash')}>
                        <Typography variant="h6">💵</Typography>
                        <Typography fontWeight="bold">מזומן</Typography>
                      </Button>
                      <Button variant="outlined" size="large" sx={{ flex: 1, minWidth: 160, py: 2, flexDirection: 'column', gap: 0.5 }}
                        onClick={() => setStep('check')}>
                        <Typography variant="h6">📋</Typography>
                        <Typography fontWeight="bold">המחאות</Typography>
                      </Button>
                      <Button variant="contained" size="large" sx={{ flex: 1, minWidth: 160, py: 2, flexDirection: 'column', gap: 0.5, bgcolor: '#1976d2' }}
                        onClick={() => handleCreditCard('pay_cc')}>
                        <Typography variant="h6">💳</Typography>
                        <Typography fontWeight="bold">תשלום באשראי</Typography>
                        <Typography variant="caption">עד 10 תשלומים</Typography>
                      </Button>
                      <Button variant="contained" size="large" sx={{ flex: 1, minWidth: 160, py: 2, flexDirection: 'column', gap: 0.5, bgcolor: '#7b1fa2' }}
                        onClick={() => handleCreditCard('pay_hok_cc')}>
                        <Typography variant="h6">🔄</Typography>
                        <Typography fontWeight="bold">הוק באשראי</Typography>
                        <Typography variant="caption">10 תשלומים עתידיים</Typography>
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            )}

            {/* Cash form */}
            {step === 'cash' && (
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Button size="small" onClick={() => setStep('method')}>← חזור</Button>
                  <Typography variant="subtitle1" fontWeight="bold">תשלום במזומן</Typography>
                  {debtAmount > 0 && (
                    <Chip sx={{ ml: 'auto' }} color="warning" variant="outlined"
                      label={`חוב לתשלום: ${debtAmount.toLocaleString()} ₪`} />
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" gap={2} mb={2}>
                  <TextField size="small" label="סכום (₪)" value={payAmount}
                    onChange={e => setPayAmount(e.target.value)} sx={{ width: 130 }}
                    error={cashExceedsDebt}
                    inputProps={{ style: { textAlign: 'right' } }} />
                  <TextField size="small" label="תאריך" type="date" value={payDate}
                    onChange={e => setPayDate(e.target.value)} InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }} />
                </Box>
                {cashExceedsDebt && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    הסכום ({Number(payAmount || 0).toLocaleString()} ₪) גבוה מהחוב בשיבוץ ({debtAmount.toLocaleString()} ₪) — לא ניתן לרשום תשלום מעל החוב.
                  </Alert>
                )}
                <TextField fullWidth multiline rows={2} size="small" label="הערות"
                  value={payNotes} onChange={e => setPayNotes(e.target.value)}
                  sx={{ mb: 2 }} inputProps={{ style: { textAlign: 'right' } }} />
                <Button fullWidth variant="contained" color="success" size="large"
                  onClick={() => handleRegisterPayment('מזומן')}
                  disabled={payLoading || !payAmount || cashExceedsDebt}>
                  {payLoading ? <CircularProgress size={22} color="inherit" /> : `רשום תשלום מזומן — ${payAmount} ₪`}
                </Button>
              </Paper>
            )}

            {/* Checks form */}
            {step === 'check' && (
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Button size="small" onClick={() => setStep('method')}>← חזור</Button>
                  <Typography variant="subtitle1" fontWeight="bold">תשלום בהמחאות</Typography>
                  {debtAmount > 0 && (
                    <Chip sx={{ ml: 'auto' }} color="warning" variant="outlined"
                      label={`חוב לתשלום: ${debtAmount.toLocaleString()} ₪`} />
                  )}
                </Box>

                {/* חיווי מוגדל של כמות ההמחאות והסכום הכולל */}
                <Box sx={{
                  display: 'flex', alignItems: 'stretch', gap: 0, mb: 2,
                  borderRadius: 2, overflow: 'hidden',
                  border: theme => `2px solid ${checksExceedDebt ? theme.palette.error.main : theme.palette.primary.main}`,
                }}>
                  <Box sx={{ flex: 1, textAlign: 'center', py: 1.5, bgcolor: '#e3f2fd' }}>
                    <Typography variant="h4" fontWeight="bold" color="primary.main" lineHeight={1}>
                      {checks.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">המחאות</Typography>
                  </Box>
                  <Box sx={{ flex: 2, textAlign: 'center', py: 1.5, bgcolor: checksExceedDebt ? '#ffebee' : '#e8f5e9' }}>
                    <Typography variant="h4" fontWeight="bold" lineHeight={1}
                      color={checksExceedDebt ? 'error.main' : 'success.main'}>
                      {totalChecksAmount.toLocaleString()} ₪
                    </Typography>
                    <Typography variant="caption" color="text.secondary">סכום כולל</Typography>
                  </Box>
                </Box>

                {duplicateCheckNumbers.size > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    קיימות המחאות עם אותו מספר (המסומנות באדום) — יש לתקן לפני רישום הקבלה.
                  </Alert>
                )}
                {checksExceedDebt && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    סך ההמחאות ({totalChecksAmount.toLocaleString()} ₪) גבוה מהחוב בשיבוץ ({debtAmount.toLocaleString()} ₪) — לא ניתן לרשום תשלום מעל החוב.
                  </Alert>
                )}

                <Box display="flex" gap={2} mb={1}>
                  <TextField size="small" label="תאריך רישום" type="date" value={payDate}
                    onChange={e => setPayDate(e.target.value)} InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }} />
                </Box>

                {/* Common fields */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa' }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>פרטים משותפים — יוחלו על המחאות חדשות</Typography>
                  <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <InputLabel>בנק</InputLabel>
                      <Select value={commonBank} onChange={e => setCommonBank(String(e.target.value))} label="בנק">
                        {banks.map(b => <MenuItem key={b.id} value={String(b.id)}>{b.name_he}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" label="סניף" value={commonBranch}
                      onChange={e => setCommonBranch(e.target.value)} sx={{ width: 90 }} />
                    <TextField size="small" label="מספר חשבון" value={commonAccount}
                      onChange={e => setCommonAccount(e.target.value)} sx={{ width: 130 }} />
                    <Button variant="outlined" size="small" onClick={applyCommonToAll}>החל על כולם</Button>
                  </Box>
                </Paper>

                {/* Checks table */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        {['#', 'מס׳ המחאה', 'תאריך', 'סכום (₪)', 'בנק', 'סניף', 'חשבון', ''].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', py: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {checks.map((c, idx) => {
                        const isDup = !!(c.checkNumber && duplicateCheckNumbers.has(c.checkNumber));
                        return (
                        <TableRow key={idx} sx={{
                          bgcolor: isDup ? '#ffebee' : 'inherit',
                          outline: isDup ? '2px solid' : 'none',
                          outlineColor: 'error.main',
                          outlineOffset: '-2px',
                        }}>
                          <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold', fontSize: 12 }}>{idx + 1}</TableCell>
                          <TableCell>
                            <TextField size="small" value={c.checkNumber}
                              onChange={e => updateCheck(idx, 'checkNumber', e.target.value)}
                              error={isDup}
                              sx={{ width: 110 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="date" value={c.checkDate}
                              onChange={e => updateCheck(idx, 'checkDate', e.target.value)}
                              InputLabelProps={{ shrink: true }} sx={{ width: 175 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={c.checkAmount}
                              onChange={e => updateCheck(idx, 'checkAmount', e.target.value)}
                              sx={{ width: 90 }} inputProps={{ style: { textAlign: 'right' } }} />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <Select value={getBankId(c.bank)} onChange={e => updateCheckBank(idx, e.target.value as number)} displayEmpty>
                                <MenuItem value=""><em>בחר</em></MenuItem>
                                {banks.map(b => <MenuItem key={b.id} value={b.id}>{b.name_he}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={c.branchNumber}
                              onChange={e => updateCheck(idx, 'branchNumber', e.target.value)} sx={{ width: 75 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={c.accountNumber}
                              onChange={e => updateCheck(idx, 'accountNumber', e.target.value)} sx={{ width: 115 }} />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeCheck(idx)} disabled={checks.length === 1} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box display="flex" gap={2} mb={2} alignItems="center">
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addCheck} size="small">הוסף המחאה</Button>
                </Box>

                <TextField fullWidth multiline rows={2} size="small" label="הערות"
                  value={payNotes} onChange={e => setPayNotes(e.target.value)}
                  sx={{ mb: 2 }} inputProps={{ style: { textAlign: 'right' } }} />

                <Button fullWidth variant="contained" color="success" size="large"
                  onClick={() => handleRegisterPayment('המחאה')}
                  disabled={
                    payLoading ||
                    checks.some(c => !c.checkNumber || !c.checkDate || !c.checkAmount) ||
                    duplicateCheckNumbers.size > 0 ||
                    checksExceedDebt
                  }>
                  {payLoading ? <CircularProgress size={22} color="inherit" /> : `רשום ${checks.length} המחאות — ${totalChecksAmount.toLocaleString()} ₪`}
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      {/* דיאלוג תוצאת תשלום — הצלחה/כשלון + לינק להורדת המסמך מטרנזילה */}
      <Dialog open={!!payResult} onClose={closePayResult} maxWidth="xs" fullWidth dir="rtl">
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {payResult?.success ? '✅ התשלום נקלט בהצלחה' : '❌ רישום התשלום נכשל'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={payResult?.success ? 'success' : 'error'} sx={{ mb: 2 }}>
            {payResult?.message}
          </Alert>
          {payResult?.retrievalKey && (
            <Button fullWidth variant="outlined" startIcon={<OpenInNewIcon />}
              onClick={() => window.open(`https://my.tranzila.com/api/get_financial_document/${payResult.retrievalKey}`, '_blank')}>
              הורדת מסמך הקבלה מטרנזילה
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayResult} variant="contained">סגור</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Main AdminDashboard ──────────────────────────────────────────────────────

const DEFAULT_SETTINGS: SystemSettings = {
  currentYear: appConfig.year,
  stations: [],
  paymentAmounts: ['1250', '625', '312'],
};

const AdminDashboard: React.FC = () => {
  const { adminRole, adminToken, adminUsername, logout } = useAdmin();

  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [students, setStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnackbar = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  // Filters (all multi-select)
  const [filterInstitution, setFilterInstitution] = useState<string[]>([]);
  const [filterGrade, setFilterGrade] = useState<string[]>([]);
  const [filterStreet, setFilterStreet] = useState<string[]>([]);
  const [filterEligibility, setFilterEligibility] = useState<string[]>([]);
  const [filterCanRegister, setFilterCanRegister] = useState<string[]>([]);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Edit drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [editData, setEditData] = useState<Student>({});
  const [saveLoading, setSaveLoading] = useState(false);

  // Bulk edit dialog
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState<Student>({});
  const [bulkSaveLoading, setBulkSaveLoading] = useState(false);

  // Add student dialog
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [newStudentData, setNewStudentData] = useState<Student>({});
  const [addStudentLoading, setAddStudentLoading] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!adminToken) return;
    setSearchLoading(true);
    setSearchError(null);
    setSelectedIds(new Set());
    try {
      const res = await fetch(
        `${appConfig.apiUrls.baseUrl}/sheets/search?q=${encodeURIComponent(searchQuery)}`,
        { headers: authHeaders(adminToken) }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      if (Array.isArray(data)) setStudents(data);
      else setSearchError(data.error || 'שגיאה בחיפוש');
    } catch { setSearchError('שגיאת תקשורת'); }
    finally { setSearchLoading(false); }
  }, [adminToken, searchQuery, logout]);

  useEffect(() => {
    if (!adminToken) return;
    handleSearch();
    // Load system settings
    fetch(`${appConfig.apiUrls.baseUrl}/settings`, { headers: authHeaders(adminToken) })
      .then(r => r.json())
      .then(d => { if (d.currentYear) setSettings(d); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived filter options
  const institutionOptions = useMemo(() =>
    [...new Set(students.map(s => s['שם מוסד משובץ']).filter(Boolean))].sort(),
    [students]
  );
  const gradeOptions = useMemo(() =>
    [...new Set(students.map(s => s['שכבה']).filter(Boolean))].sort(),
    [students]
  );
  const streetOptions = useMemo(() =>
    [...new Set(students.map(s => s['שם רחוב']).filter(Boolean))].sort(),
    [students]
  );

  // Filtered students
  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      if (filterInstitution.length && !filterInstitution.includes(s['שם מוסד משובץ'])) return false;
      if (filterGrade.length && !filterGrade.includes(s['שכבה'])) return false;
      if (filterStreet.length && !filterStreet.includes(s['שם רחוב'])) return false;
      if (filterEligibility.length) {
        const val = s['זכאות'] === 'זכאי' ? 'זכאי' : 'לא זכאי';
        if (!filterEligibility.includes(val)) return false;
      }
      if (filterCanRegister.length) {
        const val = s['האם בהסעות'] === 'כן' ? 'כן' : 'לא';
        if (!filterCanRegister.includes(val)) return false;
      }
      return true;
    });
  }, [students, filterInstitution, filterGrade, filterStreet, filterEligibility, filterCanRegister]);

  // KPIs (from all students, not filtered)
  const kpis = useMemo(() => ({
    total: students.length,
    canRegister: students.filter(s => s['האם בהסעות'] === 'כן').length,
    eligible: students.filter(s => s['זכאות'] === 'זכאי').length,
    registered: students.filter(s => s['האם נרשם'] && s['האם נרשם'] !== 'לא').length,
  }), [students]);

  // Selection helpers
  const allFilteredIds = filteredStudents.map(s => s['תעודת זהות']);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.has(id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allFilteredIds));
  };
  const toggleOne = useCallback((id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const openStudentDrawer = useCallback((student: Student) => {
    setSelectedStudent(student);
    setEditData({ ...student });
    setDrawerOpen(true);
  }, []);

  // Pagination — avoids rendering all ~4000 rows at once
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  useEffect(() => { setPage(0); }, [filteredStudents]);
  const pagedStudents = useMemo(
    () => filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredStudents, page, rowsPerPage]
  );

  const handleSaveStudent = async () => {
    if (!selectedStudent || !adminToken) return;
    setSaveLoading(true);
    try {
      const studentId = selectedStudent['תעודת זהות'];
      const res = await fetch(
        `${appConfig.apiUrls.baseUrl}/sheets/student/${studentId}`,
        { method: 'PUT', headers: authHeaders(adminToken), body: JSON.stringify(editData) }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      if (data.success) {
        showSnackbar('הפרטים עודכנו בהצלחה', 'success');
        setDrawerOpen(false);
        handleSearch();
      } else {
        showSnackbar(data.error || 'שגיאה בשמירה', 'error');
      }
    } catch { showSnackbar('שגיאת תקשורת', 'error'); }
    finally { setSaveLoading(false); }
  };

  const handleBulkSave = async () => {
    if (!adminToken || selectedIds.size === 0) return;
    setBulkSaveLoading(true);
    const payload = Object.fromEntries(Object.entries(bulkEditData).filter(([, v]) => v !== ''));
    let success = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        const res = await fetch(
          `${appConfig.apiUrls.baseUrl}/sheets/student/${id}`,
          { method: 'PUT', headers: authHeaders(adminToken), body: JSON.stringify(payload) }
        );
        if (res.status === 401) { logout(); return; }
        const data = await res.json();
        if (data.success) success++; else failed++;
      } catch { failed++; }
    }
    setBulkSaveLoading(false);
    setBulkEditOpen(false);
    setBulkEditData({});
    setSelectedIds(new Set());
    showSnackbar(`עודכנו ${success} תלמידים${failed > 0 ? `, ${failed} נכשלו` : ''}`, failed > 0 ? 'error' : 'success');
    handleSearch();
  };

  const handleAddStudent = async () => {
    if (!adminToken || !newStudentData['תעודת זהות']) return;
    setAddStudentLoading(true);
    try {
      const res = await fetch(`${appConfig.apiUrls.baseUrl}/sheets/student`, {
        method: 'POST', headers: authHeaders(adminToken), body: JSON.stringify(newStudentData),
      });
      const data = await res.json();
      if (data.success) {
        showSnackbar('תלמיד נוסף בהצלחה', 'success');
        setAddStudentOpen(false);
        setNewStudentData({});
        handleSearch();
      } else {
        showSnackbar(data.error || 'שגיאה בהוספת תלמיד', 'error');
      }
    } catch { showSnackbar('שגיאת תקשורת', 'error'); }
    finally { setAddStudentLoading(false); }
  };

  const renderEditField = (
    field: string, data: Student, setData: (d: Student) => void, required = false, idEditable = false
  ) => {
    const options = SELECT_FIELDS[field];
    if (options) {
      return (
        <FormControl fullWidth size="small" required={required}>
          <InputLabel style={{ fontSize: 12 }}>{field}</InputLabel>
          <Select
            value={data[field] || ''}
            onChange={e => setData({ ...data, [field]: e.target.value })}
            label={field}
            style={{ fontSize: 13 }}
          >
            {!required && <MenuItem value=""><em>—</em></MenuItem>}
            {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
          {required && !data[field] && <FormHelperText error>שדה חובה</FormHelperText>}
        </FormControl>
      );
    }
    return (
      <TextField
        fullWidth size="small" label={field}
        value={data[field] || ''}
        onChange={e => setData({ ...data, [field]: e.target.value })}
        disabled={field === 'תעודת זהות' && !idEditable}
        required={required}
        error={required && !data[field]}
        helperText={required && !data[field] ? 'שדה חובה' : undefined}
        inputProps={{ style: { textAlign: 'right', fontSize: 13 } }}
        InputLabelProps={{ style: { fontSize: 12 } }}
      />
    );
  };

  return (
    <Box dir="rtl" sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f0f2f5' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#1565c0', flexShrink: 0 }}>
        <Toolbar variant="dense">
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout} sx={{ flexShrink: 0 }}>יציאה</Button>
          <Chip label={adminUsername} size="small"
            sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', flexShrink: 0 }} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', mx: 2 }}>
            מערכת ניהול הסעות לוד
          </Typography>
          <Box component="img" src={lodLogo} alt="לוגו עיריית לוד" sx={{ height: 38, borderRadius: 1 }} />
        </Toolbar>
      </AppBar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white', flexShrink: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
          <Tab label="רשימת תלמידים" />
          {adminRole >= 2 && <Tab label="קליטת תשלום" />}
          {adminRole >= 2 && <Tab label="⚙ הגדרות מערכת" />}
        </Tabs>
      </Box>

      {/* ── Scrollable content area ── */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>

      {/* ── Tab 0: Student List ── */}
      {tab === 0 && (
        <Box sx={{ p: 2 }}>

          {/* KPI Row + Add button in same line */}
          <Box display="flex" gap={1.5} mb={2} alignItems="center" flexWrap="wrap">
            <KpiCard icon={<PeopleIcon />} label="סה״כ תלמידים"
              value={kpis.total} color="#1565c0" bgColor="#e3f2fd" />
            <KpiCard icon={<DirectionsBusIcon />} label="יכולים להירשם"
              value={kpis.canRegister} color="#e65100" bgColor="#fff3e0" />
            <KpiCard icon={<VerifiedIcon />} label="זכאים"
              value={kpis.eligible} color="#2e7d32" bgColor="#e8f5e9" />
            <KpiCard icon={<HowToRegIcon />} label="נרשמו בפועל"
              value={kpis.registered} color="#6a1b9a" bgColor="#f3e5f5" />
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddStudentOpen(true)}>
              הוסף תלמיד
            </Button>
          </Box>

          {/* Search + Filters */}
          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
              <TextField
                sx={{ minWidth: 220 }} size="small" label={'חיפוש לפי ת"ז, שם, מוסד...'}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                inputProps={{ style: { textAlign: 'right' } }}
              />
              <Button variant="contained" onClick={handleSearch} disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                size="small">
                חיפוש
              </Button>
              <Divider orientation="vertical" flexItem />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>מוסד</InputLabel>
                <Select multiple value={filterInstitution}
                  onChange={e => setFilterInstitution(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="מוסד" />} renderValue={renderMultiSelectSummary}>
                  {institutionOptions.map(i => (
                    <MenuItem key={i} value={i}>
                      <Checkbox checked={filterInstitution.includes(i)} size="small" />
                      <ListItemText primary={i} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>שכבה</InputLabel>
                <Select multiple value={filterGrade}
                  onChange={e => setFilterGrade(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="שכבה" />} renderValue={renderMultiSelectSummary}>
                  {gradeOptions.map(g => (
                    <MenuItem key={g} value={g}>
                      <Checkbox checked={filterGrade.includes(g)} size="small" />
                      <ListItemText primary={g} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>רחוב</InputLabel>
                <Select multiple value={filterStreet}
                  onChange={e => setFilterStreet(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="רחוב" />} renderValue={renderMultiSelectSummary}>
                  {streetOptions.map(st => (
                    <MenuItem key={st} value={st}>
                      <Checkbox checked={filterStreet.includes(st)} size="small" />
                      <ListItemText primary={st} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>האם בהסעות</InputLabel>
                <Select multiple value={filterCanRegister}
                  onChange={e => setFilterCanRegister(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="האם בהסעות" />} renderValue={renderMultiSelectSummary}>
                  {CAN_REGISTER_FILTER_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={filterCanRegister.includes(o.value)} size="small" />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>זכאות</InputLabel>
                <Select multiple value={filterEligibility}
                  onChange={e => setFilterEligibility(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="זכאות" />} renderValue={renderMultiSelectSummary}>
                  {ELIGIBILITY_FILTER_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={filterEligibility.includes(o.value)} size="small" />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', whiteSpace: 'nowrap' }}>
                {filteredStudents.length} / {students.length} תלמידים
              </Typography>
            </Box>
            {(filterInstitution.length > 0 || filterGrade.length > 0 || filterStreet.length > 0 || filterEligibility.length > 0 || filterCanRegister.length > 0) && (
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">סינון פעיל:</Typography>
                {filterInstitution.length > 0 && (
                  <Chip size="small" label={`מוסד: ${filterInstitution.join(', ')}`} onDelete={() => setFilterInstitution([])} />
                )}
                {filterGrade.length > 0 && (
                  <Chip size="small" label={`שכבה: ${filterGrade.join(', ')}`} onDelete={() => setFilterGrade([])} />
                )}
                {filterStreet.length > 0 && (
                  <Chip size="small" label={`רחוב: ${filterStreet.join(', ')}`} onDelete={() => setFilterStreet([])} />
                )}
                {filterCanRegister.length > 0 && (
                  <Chip size="small" label={`האם בהסעות: ${filterCanRegister.map(v => v === 'כן' ? 'יכולים להירשם' : 'לא בהסעות').join(', ')}`} onDelete={() => setFilterCanRegister([])} />
                )}
                {filterEligibility.length > 0 && (
                  <Chip size="small" label={`זכאות: ${filterEligibility.map(v => v === 'זכאי' ? 'זכאים' : 'לא זכאים').join(', ')}`} onDelete={() => setFilterEligibility([])} />
                )}
                <Button size="small" color="inherit" onClick={() => { setFilterInstitution([]); setFilterGrade([]); setFilterStreet([]); setFilterEligibility([]); setFilterCanRegister([]); }}>
                  נקה הכל
                </Button>
              </Box>
            )}
          </Paper>

          {/* Bulk edit bar */}
          {selectedIds.size > 0 && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#fff8e1', border: '1px solid #ffc107' }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" fontWeight="bold">
                  {selectedIds.size} תלמידים נבחרו
                </Typography>
                <Button variant="contained" size="small" startIcon={<EditIcon />}
                  onClick={() => { setBulkEditData({}); setBulkEditOpen(true); }}>
                  עריכה מרובה
                </Button>
                <Button size="small" onClick={() => setSelectedIds(new Set())}>בטל בחירה</Button>
              </Box>
            </Paper>
          )}

          {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

          {/* Table */}
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: 14 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1565c0' }}>
                  <TableCell padding="checkbox" sx={{ bgcolor: '#1565c0' }}>
                    <Checkbox checked={allSelected} indeterminate={selectedIds.size > 0 && !allSelected}
                      onChange={toggleAll} sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }} />
                  </TableCell>
                  {['ת"ז', 'שם פרטי', 'שם משפחה', 'מוסד', 'כתובת', 'שכבה', 'האם בהסעות', 'זכאות', 'סכום לתשלום', 'האם נרשם'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>עריכה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.length === 0 && !searchLoading && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {searchQuery ? 'לא נמצאו תלמידים' : 'הקש חיפוש כדי להציג תלמידים'}
                    </TableCell>
                  </TableRow>
                )}
                {pagedStudents.map(s => (
                  <StudentRow key={s['תעודת זהות']} student={s}
                    selected={selectedIds.has(s['תעודת זהות'])}
                    onToggle={toggleOne} onEdit={openStudentDrawer} />
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredStudents.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[50, 100, 200, 500]}
              labelRowsPerPage="שורות בעמוד:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} מתוך ${count}`}
            />
          </TableContainer>
        </Box>
      )}

      {/* ── Tab 1: Payment ── */}
      {tab === 1 && adminRole >= 2 && (
        <AdminPaymentTab adminToken={adminToken!} logout={logout} onSnackbar={showSnackbar} settings={settings} />
      )}

      {/* ── Tab 2: Settings ── */}
      {tab === 2 && adminRole >= 2 && (
        <AdminSettings adminToken={adminToken!} onSnackbar={showSnackbar} onSettingsChange={setSettings} />
      )}

      </Box>{/* end scrollable */}

      {/* ── Edit Drawer ── */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, p: 3, dir: 'rtl', overflowY: 'auto' } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            עריכת תלמיד — {selectedStudent?.['שם פרטי']} {selectedStudent?.['שם משפחה']}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          ת"ז: {selectedStudent?.['תעודת זהות']}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {EDITABLE_GROUPS.map(group => (
          <Box key={group.label} mb={2}>
            <Typography variant="subtitle2" color="primary" fontWeight="bold" mb={1}>{group.label}</Typography>
            <Grid container spacing={1}>
              {group.fields.map(field => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderEditField(field, editData, setEditData)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Transport & Payment - conditional logic */}
        <Box mb={2}>
          <Typography variant="subtitle2" color="primary" fontWeight="bold" mb={1}>הסעות ותשלום</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              {renderEditField('האם בהסעות', editData, setEditData)}
            </Grid>
            {editData['האם בהסעות'] === 'כן' && (
              <Grid item xs={12} sm={6}>
                {renderEditField('זכאות', editData, setEditData, true)}
              </Grid>
            )}
            {editData['האם בהסעות'] === 'כן' && editData['זכאות'] && editData['זכאות'] !== 'זכאי' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" label="סכום לתשלום (₪)" required
                  value={editData['סכום לתשלום'] || ''}
                  onChange={e => setEditData({ ...editData, 'סכום לתשלום': e.target.value })}
                  error={!editData['סכום לתשלום']}
                  helperText={!editData['סכום לתשלום'] ? 'שדה חובה' : undefined}
                  inputProps={{ style: { textAlign: 'right', fontSize: 13 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="האם נרשם"
                value={editData['האם נרשם'] || ''}
                onChange={e => setEditData({ ...editData, 'האם נרשם': e.target.value })}
                inputProps={{ style: { textAlign: 'right', fontSize: 13 } }}
                InputLabelProps={{ style: { fontSize: 12 } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="הערות" multiline rows={3}
                value={editData['הערות'] || ''}
                onChange={e => setEditData({ ...editData, 'הערות': e.target.value })}
                inputProps={{ style: { textAlign: 'right', fontSize: 13 } }}
                InputLabelProps={{ style: { fontSize: 12 } }} />
            </Grid>
          </Grid>
        </Box>

        <Box display="flex" gap={2} mt={2}>
          <Button fullWidth variant="contained" onClick={handleSaveStudent} disabled={saveLoading}>
            {saveLoading ? <CircularProgress size={22} color="inherit" /> : 'שמור שינויים'}
          </Button>
          <Button fullWidth variant="outlined" onClick={() => setDrawerOpen(false)}>ביטול</Button>
        </Box>
      </Drawer>

      {/* ── Bulk Edit Dialog ── */}
      <Dialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ dir: 'rtl' }}>
        <DialogTitle sx={{ textAlign: 'right' }}>עריכה מרובה — {selectedIds.size} תלמידים</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            ישנו שדה שמולא? הוא יעודכן לכל התלמידים הנבחרים. שדות ריקים יישארו ללא שינוי.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>האם בהסעות</InputLabel>
                <Select value={bulkEditData['האם בהסעות'] || ''}
                  onChange={e => {
                    const value = e.target.value;
                    setBulkEditData(value === 'כן'
                      ? { ...bulkEditData, 'האם בהסעות': value }
                      : { ...bulkEditData, 'האם בהסעות': value, 'זכאות': '', 'סכום לתשלום': '' });
                  }}
                  label="האם בהסעות">
                  <MenuItem value=""><em>ללא שינוי</em></MenuItem>
                  <MenuItem value="כן">כן</MenuItem>
                  <MenuItem value="לא">לא</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {bulkEditData['האם בהסעות'] === 'כן' && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>זכאות</InputLabel>
                  <Select value={bulkEditData['זכאות'] || ''}
                    onChange={e => {
                      const value = e.target.value;
                      setBulkEditData(value === 'לא זכאי'
                        ? { ...bulkEditData, 'זכאות': value }
                        : { ...bulkEditData, 'זכאות': value, 'סכום לתשלום': '' });
                    }}
                    label="זכאות">
                    <MenuItem value=""><em>ללא שינוי</em></MenuItem>
                    <MenuItem value="זכאי">זכאי</MenuItem>
                    <MenuItem value="לא זכאי">לא זכאי</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {bulkEditData['האם בהסעות'] === 'כן' && bulkEditData['זכאות'] === 'לא זכאי' && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>סכום לתשלום</InputLabel>
                  <Select value={bulkEditData['סכום לתשלום'] || ''}
                    onChange={e => setBulkEditData({ ...bulkEditData, 'סכום לתשלום': e.target.value })}
                    label="סכום לתשלום">
                    <MenuItem value=""><em>ללא שינוי</em></MenuItem>
                    {settings.paymentAmounts.map(a => (
                      <MenuItem key={a} value={a}>{Number(a).toLocaleString()} ₪</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleBulkSave} disabled={bulkSaveLoading || Object.values(bulkEditData).every(v => !v)}>
            {bulkSaveLoading ? <CircularProgress size={20} color="inherit" /> : `עדכן ${selectedIds.size} תלמידים`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Student Dialog ── */}
      <Dialog open={addStudentOpen} onClose={() => setAddStudentOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ dir: 'rtl' }}>
        <DialogTitle sx={{ textAlign: 'right' }}>הוספת תלמיד חדש</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {[
              { field: 'תעודת זהות', required: true },
              { field: 'שם פרטי', required: true },
              { field: 'שם משפחה', required: true },
              { field: 'מגדר', required: false },
              { field: 'שכבה', required: false },
              { field: 'תאריך לידה', required: false },
              { field: 'מוסד משובץ', required: true },
              { field: 'שם מוסד משובץ', required: true },
              { field: 'שם רחוב', required: true },
              { field: 'מספר בית', required: true },
              { field: 'שם עיר', required: true },
              { field: 'שם אב', required: false },
              { field: 'שם משפחה אב', required: false },
              { field: 'טלפון נייד אב', required: false },
              { field: 'תז אב', required: false },
              { field: 'שם אם', required: false },
              { field: 'טלפון נייד אם', required: false },
            ].map(({ field, required }) => (
              <Grid item xs={12} sm={4} key={field}>
                {renderEditField(field, newStudentData, setNewStudentData, required, field === 'תעודת זהות')}
              </Grid>
            ))}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>הסעות ותשלום</Divider>
            </Grid>
            <Grid item xs={12} sm={4}>
              {renderEditField('האם בהסעות', newStudentData, setNewStudentData, true)}
            </Grid>
            {newStudentData['האם בהסעות'] === 'כן' && (
              <Grid item xs={12} sm={4}>
                {renderEditField('זכאות', newStudentData, setNewStudentData, true)}
              </Grid>
            )}
            {newStudentData['האם בהסעות'] === 'כן' && newStudentData['זכאות'] && newStudentData['זכאות'] !== 'זכאי' && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="סכום לתשלום (₪)" required
                  value={newStudentData['סכום לתשלום'] || ''}
                  onChange={e => setNewStudentData({ ...newStudentData, 'סכום לתשלום': e.target.value })}
                  error={!newStudentData['סכום לתשלום']}
                  helperText={!newStudentData['סכום לתשלום'] ? 'שדה חובה' : undefined}
                  inputProps={{ style: { textAlign: 'right' } }} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'row-reverse', justifyContent: 'flex-start', gap: 1 }}>
          <Button variant="contained" onClick={handleAddStudent}
            disabled={
              addStudentLoading ||
              !newStudentData['תעודת זהות'] ||
              !newStudentData['שם פרטי'] ||
              !newStudentData['שם משפחה'] ||
              !newStudentData['מוסד משובץ'] ||
              !newStudentData['שם מוסד משובץ'] ||
              !newStudentData['שם רחוב'] ||
              !newStudentData['מספר בית'] ||
              !newStudentData['שם עיר'] ||
              !newStudentData['האם בהסעות'] ||
              (newStudentData['האם בהסעות'] === 'כן' && !newStudentData['זכאות']) ||
              (newStudentData['האם בהסעות'] === 'כן' && newStudentData['זכאות'] !== 'זכאי' && !newStudentData['סכום לתשלום'])
            }>
            {addStudentLoading ? <CircularProgress size={20} color="inherit" /> : 'הוסף תלמיד'}
          </Button>
          <Button onClick={() => { setAddStudentOpen(false); setNewStudentData({}); }}>ביטול</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
